import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Brain, TrendingUp, AlertCircle, Target, Clock, 
  Activity, BarChart3, Zap, Calendar, Globe
} from 'lucide-react';
import { useEnhancedCredits } from '@/hooks/useEnhancedCredits';
import { useWorkspaceCredits } from '@/hooks/useWorkspaceCredits';

export const PredictiveInsights: React.FC = () => {
  const { insights } = useEnhancedCredits();
  const { creditWallet } = useWorkspaceCredits();

  const getDayName = (dayIndex: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayIndex] || 'Unknown';
  };

  const getHourDisplay = (hour: number) => {
    if (hour === null || hour === undefined) return 'Unknown';
    return hour === 0 ? '12:00 AM' : hour < 12 ? `${hour}:00 AM` : `${hour - 12}:00 PM`;
  };

  const getPredictionAccuracy = () => {
    if (!insights.forecast) return 0;
    
    // Simulate accuracy based on data quality
    const dataPoints = insights.forecast.daily > 0 ? 100 : 50;
    return Math.min(95, dataPoints);
  };

  const getBalanceHealth = () => {
    if (!creditWallet || !insights.forecast) return 'unknown';
    
    const daysRemaining = insights.forecast.daysRemaining;
    if (daysRemaining > 30) return 'excellent';
    if (daysRemaining > 14) return 'good';
    if (daysRemaining > 7) return 'warning';
    return 'critical';
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'excellent':
        return 'text-success bg-success/10';
      case 'good':
        return 'text-primary bg-primary/10';
      case 'warning':
        return 'text-warning bg-warning/10';
      case 'critical':
        return 'text-destructive bg-destructive/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const balanceHealth = getBalanceHealth();
  const accuracy = getPredictionAccuracy();

  return (
    <div className="space-y-6">
      {/* Prediction Accuracy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Brain className="h-5 w-5" />
            <span>AI Predictions</span>
            <Badge variant="secondary" className="ml-auto">
              {accuracy}% accuracy
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Balance Health */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Balance Health</span>
              </div>
              <Badge className={getHealthColor(balanceHealth)} variant="secondary">
                {balanceHealth.charAt(0).toUpperCase() + balanceHealth.slice(1)}
              </Badge>
              <p className="text-xs text-muted-foreground mt-1">
                {insights.forecast?.daysRemaining || 0} days remaining
              </p>
            </div>

            {/* Usage Trend */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Usage Trend</span>
              </div>
              <Badge variant="outline" className="capitalize">
                {insights.trend || 'Stable'}
              </Badge>
              <p className="text-xs text-muted-foreground mt-1">
                Based on 14-day analysis
              </p>
            </div>

            {/* Anomaly Risk */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Anomaly Risk</span>
              </div>
              <Badge variant={insights.anomalyScore > 60 ? "destructive" : "secondary"}>
                {Math.round(insights.anomalyScore)}%
              </Badge>
              <p className="text-xs text-muted-foreground mt-1">
                Unusual pattern detection
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Patterns */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Usage Patterns</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Peak Usage Times */}
            <div>
              <h4 className="font-medium mb-3">Peak Usage Times</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-accent rounded">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Peak Hour</span>
                  </div>
                  <span className="font-medium">
                    {getHourDisplay(insights.peakUsageHour)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-accent rounded">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Peak Day</span>
                  </div>
                  <span className="font-medium">
                    {getDayName(insights.peakUsageDay)}
                  </span>
                </div>
              </div>
            </div>

            {/* Optimization Opportunities */}
            <div>
              <h4 className="font-medium mb-3">Optimization</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-success/10 rounded">
                  <div className="flex items-center space-x-2">
                    <Target className="h-4 w-4 text-success" />
                    <span className="text-sm">Efficiency Score</span>
                  </div>
                  <span className="font-medium text-success">
                    {insights.efficiency.score}%
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-primary/10 rounded">
                  <div className="flex items-center space-x-2">
                    <Zap className="h-4 w-4 text-primary" />
                    <span className="text-sm">Savings Potential</span>
                  </div>
                  <span className="font-medium text-primary">
                    ${insights.optimizationPotential}/mo
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Efficiency Suggestions */}
          {insights.efficiency.suggestions.length > 0 && (
            <div className="mt-6">
              <h4 className="font-medium mb-3">AI Recommendations</h4>
              <div className="space-y-2">
                {insights.efficiency.suggestions.map((suggestion, index) => (
                  <Alert key={index}>
                    <Brain className="h-4 w-4" />
                    <AlertDescription>{suggestion}</AlertDescription>
                  </Alert>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Forecast Details */}
      {insights.forecast && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Globe className="h-5 w-5" />
              <span>Usage Forecast</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 border rounded-lg">
                <p className="text-sm text-muted-foreground">Daily Average</p>
                <p className="text-lg font-bold text-foreground">
                  {insights.forecast.daily}
                </p>
                <p className="text-xs text-muted-foreground">credits/day</p>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <p className="text-sm text-muted-foreground">Weekly Estimate</p>
                <p className="text-lg font-bold text-foreground">
                  {insights.forecast.weekly}
                </p>
                <p className="text-xs text-muted-foreground">credits/week</p>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <p className="text-sm text-muted-foreground">Monthly Projection</p>
                <p className="text-lg font-bold text-foreground">
                  {insights.forecast.monthly}
                </p>
                <p className="text-xs text-muted-foreground">credits/month</p>
              </div>
              <div className="text-center p-3 border rounded-lg">
                <p className="text-sm text-muted-foreground">Balance Duration</p>
                <p className="text-lg font-bold text-foreground">
                  {insights.forecast.daysRemaining}
                </p>
                <p className="text-xs text-muted-foreground">days left</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};